/**
 * Maps advanced validation properties from the column item to a nested component object for compilation.
 * @param {Object} item - The column item to process
 */
const compileMapping = item => {
	item.component = {
		"minimum-error-message": item["minimum-error-message"],
		"maximum-error-message": item["maximum-error-message"],
		"minimum-length": item["minimum-length"],
		"maximum-length": item["maximum-length"],
		"minimum-items-error-message": item["minimum-items-error-message"],
		"maximum-items-error-message": item["maximum-items-error-message"],
		"minimum-items": item["minimum-items"],
		"maximum-items": item["maximum-items"],
		"custom-validations": item["custom-validations"],
		"table-fraction-size": item["table-fraction-size"],
		"date-validation-minimum": item["date-validation-minimum"],
		"date-operator-minimum": item["date-operator-minimum"],
		"date-value-minimum": item["date-value-minimum"],
		"date-unit-minimum": item["date-unit-minimum"],
		"month-minimum": item["month-minimum"],
		"day-minimum": item["day-minimum"],
		"year-minimum": item["year-minimum"],
		"date-validation-maximum": item["date-validation-maximum"],
		"date-operator-maximum": item["date-operator-maximum"],
		"date-value-maximum": item["date-value-maximum"],
		"date-unit-maximum": item["date-unit-maximum"],
		"month-maximum": item["month-maximum"],
		"day-maximum": item["day-maximum"],
		"year-maximum": item["year-maximum"],
		"table-items": item["table-items"],
		"table-decimal-value": item["table-decimal-value"],
		"expression-format": item["expression-format"],
		"expression-fraction-size": item["expression-fraction-size"],
		"multi-select": item["multi-select"],
		required: item.required,
		format: item.format,
		expression: item.expression,
	};

	return item;
};

/**
 * Maps advanced validation properties from a nested component object back to the column item for decompilation.
 * @param {Object} item - The column item to process
 */
const decompileMapping = item => {
	item.format = item.component?.format;
	item.required = item.component?.required;
	item.expression = item.component?.expression;
	item["minimum-error-message"] = item.component?.["minimum-error-message"];
	item["maximum-error-message"] = item.component?.["maximum-error-message"];
	item["minimum-length"] = item.component?.["minimum-length"];
	item["maximum-length"] = item.component?.["maximum-length"];
	item["minimum-items-error-message"] = item.component?.["minimum-items-error-message"];
	item["maximum-items-error-message"] = item.component?.["maximum-items-error-message"];
	item["minimum-items"] = item.component?.["minimum-items"];
	item["maximum-items"] = item.component?.["maximum-items"];
	item["custom-validations"] = item.component?.["custom-validations"];
	item["table-fraction-size"] = item.component?.["table-fraction-size"];
	item["date-validation-minimum"] = item.component?.["date-validation-minimum"];
	item["date-operator-minimum"] = item.component?.["date-operator-minimum"];
	item["date-value-minimum"] = item.component?.["date-value-minimum"];
	item["date-unit-minimum"] = item.component?.["date-unit-minimum"];
	item["month-minimum"] = item.component?.["month-minimum"];
	item["day-minimum"] = item.component?.["day-minimum"];
	item["year-minimum"] = item.component?.["year-minimum"];
	item["date-validation-maximum"] = item.component?.["date-validation-maximum"];
	item["date-operator-maximum"] = item.component?.["date-operator-maximum"];
	item["date-value-maximum"] = item.component?.["date-value-maximum"];
	item["date-unit-maximum"] = item.component?.["date-unit-maximum"];
	item["month-maximum"] = item.component?.["month-maximum"];
	item["day-maximum"] = item.component?.["day-maximum"];
	item["year-maximum"] = item.component?.["year-maximum"];
	item["table-items"] = item.component?.["table-items"];
	item["table-decimal-value"] = item.component?.["table-decimal-value"];
	item["expression-format"] = item.component?.["expression-format"];
	item["expression-fraction-size"] = item.component?.["expression-fraction-size"];
	item["multi-select"] = item.component?.["multi-select"];

	return item;
};

/**
 * Recursively processes all childShapes to find and transform column items
 * @param {Object} shape - The shape object to process
 * @param {boolean} compile - Flag to determine if we are compiling or decompiling
 */
const processChildShapesRecursively = (shape, compile) => {
	if (!shape) {return;}

	// Check if current shape has columns.items to process
	if (shape.properties?.columns?.items) {
		shape.properties.columns.items.forEach(item => {
			if (compile) {
				compileMapping(item);
			} else {
				decompileMapping(item);
			}
		});
	}

	// Recursively process childShapes
	if (Array.isArray(shape.childShapes)) {
		shape.childShapes.forEach(childShape => {
			processChildShapesRecursively(childShape, compile);
		});
	}
};

export const compileDataTable = ({ url, method, data }) => {
	if(url.startsWith("./app/model/FORM_MODEL-") && method === "PUT") {
		const payload = data.split("&svg_xml=");
		const decodedData =  JSON.parse(decodeURIComponent(payload[0].replace("json_xml=", "").replaceAll("+", "plusSymbolToReplace")));

		// Process all childShapes recursively
		processChildShapesRecursively(decodedData, true);
		const reEncoded = JSON.stringify(decodedData);
		const plusEncoded = encodeURIComponent(reEncoded).replaceAll("plusSymbolToReplace", "+");

		return `json_xml=${plusEncoded}&svg_xml=${payload[1]}`;
	}

	return data;
};

export const decompileDataTable = response => {
	if(response.config.url.includes("./app/editor") && response.config.method === "GET" && response.data?.model?.childShapes) {
		processChildShapesRecursively(response.data.model, false);
	}

	return response.data.model;
};